import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { getStudentType, calculateTimelineMessage } from '../../utils/collegeTimelineCalculator';
import { saveFormattedResults, renderResultsToHtml } from '../../utils/resultsStorage';
import { StrategicSummaryService } from '../../services/StrategicSummaryService';
import StrategicResultsRenderer from './shared/StrategicResultsRenderer';
import { 
  CheckCircle, 
  TrendingUp, 
  Lightbulb, 
  Target, 
  ArrowRight,
  Save,
  Download,
  Edit3,
  RefreshCw,
  Brain,
  Heart,
  Zap,
  Compass,
  Share2,
  Users,
  Eye,
  X,
  Link2,
  History,
  Plus
} from 'lucide-react';

// Simple markdown processor for formatting text
const processMarkdown = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  return text
    // Bold: **text** -> <strong>text</strong>
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic: *text* -> <em>text</em>
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Code: `text` -> <code>text</code>
    .replace(/`(.*?)`/g, '<code>$1</code>')
    // Line breaks
    .replace(/\n/g, '<br>');
};

const ConversationalResults = () => {
  const navigate = useNavigate();
  const { currentUser } = useFamilyContext();
  const { addCollege, addEssay, addTimelineItem } = useCollegeContext();
  const [showCompletion, setShowCompletion] = useState(false); // Disabled - ConversationalAssessment handles completion animation
  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const [dataSource, setDataSource] = useState('unknown'); // Track where data came from
  const [isValidating, setIsValidating] = useState(false); // Prevent repeated validation calls
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [sharingPermissions, setSharingPermissions] = useState({
    parentCanView: false,
    sharedAt: null,
    notifyParent: true
  });
  const [studentGradeLevel, setStudentGradeLevel] = useState('');
  const [resultsPermalink, setResultsPermalink] = useState(null);
  const [isSavingResults, setIsSavingResults] = useState(false);
  const [savedSuccessfully, setSavedSuccessfully] = useState(false);
  
  // Integration state
  const [integrationStatus, setIntegrationStatus] = useState({
    colleges: {},
    essays: {},
    timeline: {}
  });
  const [isIntegrating, setIsIntegrating] = useState(false);
  
  // Check if in testing mode
  const isTestingMode = localStorage.getItem('testing-mode') === 'true';
  // Only log testing mode once or when it changes, not on every render
  const [loggedTestingMode, setLoggedTestingMode] = useState(null);
  if (loggedTestingMode !== isTestingMode) {
    console.log('🔍 ConversationalResults - Testing mode:', isTestingMode);
    setLoggedTestingMode(isTestingMode);
  }

  // Clean up polling when component unmounts
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, []);

  // Load insights when component mounts or user changes
  useEffect(() => {
    console.log('🔍 ConversationalResults: Loading insights for user:', currentUser?.id);
    
    // Clear any existing polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    
    // Reset state and load insights
    setInsights(null);
    setIsLoading(true);
    loadInsights();
    loadStudentProfile();
    
    // Set up polling for insights if they don't load immediately
    pollIntervalRef.current = setInterval(() => {
      console.log('🔍 ConversationalResults: Polling for insights...');
      loadInsights();
    }, 3000);
    
    // Stop polling after 30 seconds
    pollTimeoutRef.current = setTimeout(() => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        console.log('🔍 ConversationalResults: Stopped polling after timeout');
      }
    }, 30000);
    
  }, [currentUser?.id]);

  // Stop polling when insights are loaded and save results
  useEffect(() => {
    if (insights) {
      console.log('✅ ConversationalResults: Insights loaded, stopping polling');
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      setIsLoading(false);
      
      // Auto-save formatted results for permanent storage
      saveResultsPermanently();
    }
  }, [insights]);

  // Note: Legacy validation removed - system now uses strategic format exclusively

  // Enhanced data validation function for insights - supports both markdown and JSON formats
  const validateEnhancedInsightsData = (data) => {
    console.log('🔍 ConversationalResults: Validating data:', {
      type: typeof data,
      isString: typeof data === 'string',
      isObject: typeof data === 'object',
      keys: typeof data === 'object' ? Object.keys(data) : 'N/A',
      length: typeof data === 'string' ? data.length : 'N/A',
      preview: typeof data === 'string' ? data.substring(0, 100) : JSON.stringify(data).substring(0, 100)
    });

    if (!data) {
      console.log('❌ ConversationalResults: No data provided');
      return false;
    }
    
    // Define validation variables outside conditionals to avoid scope issues
    let hasStrategicAdvantage = false;
    let hasIntellectualSignature = false;
    let hasHiddenSuperpowers = false;
    
    // Handle structured JSON object format (preferred format from format-insights API)
    if (typeof data === 'object' && data !== null) {
      // Check for any malformed markdown artifacts that might indicate parsing issues
      const hasMarkdownArtifacts = JSON.stringify(data).includes('**') || 
                                   JSON.stringify(data).includes('##') ||
                                   JSON.stringify(data).includes('```');
      
      if (hasMarkdownArtifacts) {
        console.warn('⚠️ ConversationalResults: JSON contains markdown artifacts, may need cleaning');
      }
      
      // Check for NEW nested structure (8-section format with insights wrapper)
      // Note: studentProfile might be missing when loading from database
      if (data.insights && typeof data.insights === 'object') {
        console.log('🎯 ConversationalResults: Detected new nested structure with insights wrapper');
        
        const insights = data.insights;
        
        // Check for critical sections - be more lenient for database-loaded data
        hasStrategicAdvantage = insights.strategicAdvantage && 
                               typeof insights.strategicAdvantage === 'object';
        
        hasIntellectualSignature = insights.intellectualSignature && 
                                  typeof insights.intellectualSignature === 'object';
        
        hasHiddenSuperpowers = insights.hiddenSuperpowers && 
                              typeof insights.hiddenSuperpowers === 'object';
        
        const hasStrategicPositioning = insights.strategicPositioning && 
                                       typeof insights.strategicPositioning === 'object';
        const hasTacticalAdvantages = insights.tacticalAdvantages && 
                                     typeof insights.tacticalAdvantages === 'object';
        const hasEssayStrategy = insights.essayStrategy && 
                                typeof insights.essayStrategy === 'object';
        const hasCollegeRecommendations = insights.collegeRecommendations && 
                                         typeof insights.collegeRecommendations === 'object';
        const hasActionPlan = insights.actionPlan && 
                             (Array.isArray(insights.actionPlan) || typeof insights.actionPlan === 'object');
        const hasSuccessMetrics = insights.successMetrics && 
                                 typeof insights.successMetrics === 'object';
        
        // More lenient validation - just need the core sections
        const isFullyStructured = hasStrategicAdvantage && 
                                 hasIntellectualSignature && 
                                 hasHiddenSuperpowers;
        
        if (isFullyStructured) {
          console.log('✅ ConversationalResults: New 8-section nested format validation passed');
          return true;
        } else {
          console.log('⚠️ ConversationalResults: New format detected but missing sections', {
            hasStrategicAdvantage,
            hasIntellectualSignature,
            hasHiddenSuperpowers,
            hasStrategicPositioning,
            hasTacticalAdvantages,
            hasEssayStrategy,
            hasCollegeRecommendations,
            hasActionPlan,
            hasSuccessMetrics
          });
          // Still return true if we have the core sections
          return hasStrategicAdvantage && hasIntellectualSignature && hasHiddenSuperpowers;
        }
      }
      
      // Fallback: Check for OLD flat structure (7-section format)
      hasStrategicAdvantage = (data.strategicAdvantage && typeof data.strategicAdvantage === 'string' && data.strategicAdvantage.length > 10) ||
                              (data.admissionsPositioningStrategy && typeof data.admissionsPositioningStrategy === 'string' && data.admissionsPositioningStrategy.length > 10);
      
      hasIntellectualSignature = (data.intellectualSignature && 
                                  typeof data.intellectualSignature === 'object' &&
                                  data.intellectualSignature.title &&
                                  data.intellectualSignature.content) ||
                                 (data.coreIntellectualSignature && typeof data.coreIntellectualSignature === 'string' && data.coreIntellectualSignature.length > 10);
      
      hasHiddenSuperpowers = data.hiddenSuperpowers && 
                             (typeof data.hiddenSuperpowers === 'object') &&
                             (data.hiddenSuperpowers.superpowers || data.hiddenSuperpowers.content);
      
      // Additional structural validation for old format
      const hasCollegeRecommendations = data.collegeRecommendations && typeof data.collegeRecommendations === 'object';
      const hasActionPlan = data.actionPlan && Array.isArray(data.actionPlan) && data.actionPlan.length > 0;
      const hasEssayStrategy = data.essayStrategy && typeof data.essayStrategy === 'object';
      
      const isValid = hasStrategicAdvantage && hasIntellectualSignature && hasHiddenSuperpowers;
      const isFullyStructured = isValid && hasCollegeRecommendations && hasActionPlan && hasEssayStrategy;
      
      if (isFullyStructured) {
        console.log('✅ ConversationalResults: Old flat JSON format validation passed');
        return true;
      } else if (isValid) {
        console.log('✅ ConversationalResults: Basic old format validation passed');
        return true;
      } else {
        console.log('❌ ConversationalResults: JSON format validation failed - missing required fields', {
          strategicAdvantage: hasStrategicAdvantage,
          intellectualSignature: hasIntellectualSignature,
          hiddenSuperpowers: hasHiddenSuperpowers,
          collegeRecommendations: hasCollegeRecommendations,
          actionPlan: hasActionPlan,
          essayStrategy: hasEssayStrategy,
          dataKeys: Object.keys(data),
          hasMarkdownArtifacts
        });
        return false;
      }
    }
    
    // Handle markdown string format (fallback format for backward compatibility)
    if (typeof data === 'string') {
      // More comprehensive markdown validation
      const hasMarkdownHeaders = data.includes('##') || data.includes('#');
      const hasContent = data.length > 100;
      const hasKeyWords = data.toLowerCase().includes('strategic') || 
                          data.toLowerCase().includes('advantage') ||
                          data.toLowerCase().includes('signature') ||
                          data.toLowerCase().includes('superpower');
      
      // Check for empty content indicators
      const hasEmptyPlaceholders = data.includes('TBD') || 
                                   data.includes('TODO') ||
                                   data.includes('[placeholder]') ||
                                   data.includes('...') && data.length < 200;
      
      // Check for API error indicators
      const hasAPIErrors = data.includes('error') || 
                           data.includes('failed') ||
                           data.includes('timeout') ||
                           data.includes('500') ||
                           data.includes('400');
      
      const isValid = hasMarkdownHeaders && hasContent && hasKeyWords && !hasEmptyPlaceholders && !hasAPIErrors;
      
      if (isValid) {
        console.log('✅ ConversationalResults: Markdown format validation passed');
        return true;
      } else {
        console.log('❌ ConversationalResults: Markdown format validation failed', {
          hasMarkdownHeaders,
          hasContent,
          hasKeyWords,
          hasEmptyPlaceholders,
          hasAPIErrors,
          length: data.length,
          preview: data.substring(0, 200)
        });
        return false;
      }
    }
    
    console.log('❌ ConversationalResults: Unknown data format - neither valid string nor object');
    return false;
  };

  // Parse markdown string into structured format for UI compatibility
  const parseMarkdownToStructuredInsights = (markdownString) => {
    if (typeof markdownString !== 'string') {
      return markdownString; // Return as-is if already structured
    } 

    console.log('🔄 ConversationalResults: Parsing markdown to structured format');
    console.log('📝 Markdown preview (first 500 chars):', markdownString.substring(0, 500));
    
    // Parse the markdown content to extract real sections
    const sections = {};
    const lines = markdownString.split('\n');
    let currentSection = null;
    let currentContent = [];
    
    console.log('📝 Total lines to parse:', lines.length);
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Identify main sections - Enhanced to handle various formats
      if (line.startsWith('# YOUR STRATEGIC ADVANTAGE') || line.startsWith('## 1. YOUR TRANSFER ADVANTAGE') || line.includes('STRATEGIC ADVANTAGE') || line.includes('TRANSFER ADVANTAGE')) {
        if (currentSection) sections[currentSection] = currentContent.join('\n').trim();
        currentSection = 'strategicAdvantage';
        currentContent = [];
      } else if (line.includes('HOW YOUR BRAIN WORKS') || line.includes('Intellectual Signature') || line.includes('HOW YOU THINK DIFFERENTLY') || line.includes('## 2.')) {
        if (currentSection) sections[currentSection] = currentContent.join('\n').trim();
        currentSection = 'intellectualSignature';
        currentContent = [];
      } else if (line.includes('SECRET WEAPONS') || line.includes('Hidden Superpowers') || line.includes('DISTINCTIVE EDGE') || line.includes('YOUR DISTINCTIVE EDGE') || line.includes('## 3.')) {
        if (currentSection) sections[currentSection] = currentContent.join('\n').trim();
        currentSection = 'hiddenSuperpowers';
        currentContent = [];
      } else if (line.includes('HOW TO STAND OUT') || line.includes('Strategic Positioning') || line.includes('HOW YOU CHANGE THE GAME') || line.includes('CHANGE THE GAME') || line.includes('## 4.')) {
        if (currentSection) sections[currentSection] = currentContent.join('\n').trim();
        currentSection = 'strategicPositioning';
        currentContent = [];
      } else if (line.includes('ESSAY GAME PLAN') || line.includes('Essay Strategy') || line.includes('TRANSFER NARRATIVE STRATEGY') || line.includes('NARRATIVE STRATEGY') || line.includes('## 5.')) {
        if (currentSection) sections[currentSection] = currentContent.join('\n').trim();
        currentSection = 'essayStrategy';
        currentContent = [];
      } else if (line.includes('COLLEGE RECOMMENDATIONS') || line.includes('TRANSFER-FRIENDLY PROGRAMS') || line.includes('FRIENDLY PROGRAMS') || line.includes('## 6.')) {
        if (currentSection) sections[currentSection] = currentContent.join('\n').trim();
        currentSection = 'collegeRecommendations';
        currentContent = [];
      } else if (line.includes('YOUR NEXT STEPS') || line.includes('Action Plan') || line.includes('NEXT STEPS') || line.includes('## 7.')) {
        if (currentSection) sections[currentSection] = currentContent.join('\n').trim();
        currentSection = 'actionPlan';
        currentContent = [];
      } else if (currentSection && line && !line.startsWith('#') && !line.startsWith('##')) {
        currentContent.push(line);
      }
    }
    
    // Don't forget the last section
    if (currentSection) sections[currentSection] = currentContent.join('\n').trim();

    console.log('📝 Parsed sections:', Object.keys(sections));
    console.log('📝 Section contents preview:');
    Object.entries(sections).forEach(([key, value]) => {
      console.log(`  - ${key}: ${value?.substring(0, 100)}...`);
    });

    // Extract strategic advantage text - Enhanced for various formats
    const strategicAdvantageText = sections.strategicAdvantage || 
      markdownString.match(/# YOUR (?:STRATEGIC ADVANTAGE|TRANSFER ADVANTAGE)\s*\n\n(.+?)(?=\n##|\n#|$)/s)?.[1]?.trim() ||
      markdownString.match(/## 1\. YOUR TRANSFER ADVANTAGE\s*[\s\S]*?\n\n(.+?)(?=\n---|\n##|$)/s)?.[1]?.trim() ||
      markdownString.match(/Your Strategic Positioning:\s*(.+?)(?=\n---|\n##|$)/s)?.[1]?.trim() ||
      "Your unique combination of skills makes you stand out from other applicants.";

    // Parse intellectual signature content
    const intellectualContent = sections.intellectualSignature || "";
    const intellectualTitle = intellectualContent.match(/## \d+\. (.+?)(?:\n|$)/)?.[1] || "How Your Brain Works";
    
    // Parse superpowers - Enhanced for various formats
    const superpowersContent = sections.hiddenSuperpowers || "";
    const superpowers = [];
    
    // Try different formats for superpowers
    let superpowerMatches = superpowersContent.match(/\*\*([^*]+)\*\*:\s*([^\n]+)/g);
    
    // If no matches, try the format "Title: Description" without bold
    if (!superpowerMatches || superpowerMatches.length === 0) {
      superpowerMatches = superpowersContent.match(/^([^:\n]+):\s*([^\n]+)/gm);
    }
    
    // If still no matches, try extracting from "✨ Your core strengths" section
    if (!superpowerMatches || superpowerMatches.length === 0) {
      const coreStrengthsMatch = superpowersContent.match(/✨\s*Your core strengths[\s\S]*?(?=\n>|$)/);
      if (coreStrengthsMatch) {
        superpowerMatches = coreStrengthsMatch[0].match(/^([A-Z][^:\n]+):\s*([^\n]+)/gm);
      }
    }
    
    if (superpowerMatches) {
      superpowerMatches.forEach(match => {
        const parts = match.replace(/\*\*/g, '').split(':');
        if (parts.length >= 2) {
          superpowers.push(`${parts[0].trim()}: ${parts[1].trim()}`);
        }
      });
    }

    // Parse essay topics - Enhanced for various formats
    const essayContent = sections.essayStrategy || "";
    const essayTopics = [];
    
    // Try multiple formats for essay topics
    let essayMatches = essayContent.match(/^[•\-\*]\s*(.+)$/gm);
    
    // If no bullet points, try looking for "➤ Essay topics" section
    if (!essayMatches || essayMatches.length === 0) {
      const essayTopicsMatch = essayContent.match(/➤\s*Essay topics[\s\S]*?(?=\n⚠️|$)/);
      if (essayTopicsMatch) {
        essayMatches = essayTopicsMatch[0].match(/^([A-Z][^:\n]+):\s*([^\n]+)/gm);
      }
    }
    
    // Try looking for numbered or titled topics
    if (!essayMatches || essayMatches.length === 0) {
      essayMatches = essayContent.match(/^([A-Z][^:\n]+):\s*([^\n]+)/gm);
    }
    
    if (essayMatches) {
      essayMatches.forEach(match => {
        const topic = match.replace(/^[•\-\*➤]\s*/, '').trim();
        if (topic && !topic.includes('avoid') && !topic.includes('trap') && !topic.includes('AVOID')) {
          essayTopics.push(topic);
        }
      });
    }

    // Parse college recommendations - Enhanced for various formats
    const collegeContent = sections.collegeRecommendations || "";
    const reachSchools = [];
    const targetSchools = [];
    const safetySchools = [];
    
    // Try multiple formats for school sections
    const schoolSections = collegeContent.split(/(?=REACH SCHOOLS|TARGET SCHOOLS|SAFETY SCHOOLS|🚀\s*Reach Schools|🎯\s*Target Schools|✅\s*Safety Schools)/i);
    
    schoolSections.forEach(section => {
      if (section.includes('REACH SCHOOLS') || section.includes('🚀') || section.includes('Reach Schools')) {
        // Try bullet points first
        let matches = section.match(/^[•\-\*]\s*(.+)$/gm);
        // If no bullet points, try school names followed by colons
        if (!matches || matches.length === 0) {
          matches = section.match(/^([A-Z][^:\n]+):\s*([^\n]+)/gm);
        }
        if (matches) matches.forEach(match => reachSchools.push(match.replace(/^[•\-\*]\s*/, '').trim()));
      } else if (section.includes('TARGET SCHOOLS') || section.includes('🎯') || section.includes('Target Schools')) {
        let matches = section.match(/^[•\-\*]\s*(.+)$/gm);
        if (!matches || matches.length === 0) {
          matches = section.match(/^([A-Z][^:\n]+):\s*([^\n]+)/gm);
        }
        if (matches) matches.forEach(match => targetSchools.push(match.replace(/^[•\-\*]\s*/, '').trim()));
      } else if (section.includes('SAFETY SCHOOLS') || section.includes('✅') || section.includes('Safety Schools')) {
        let matches = section.match(/^[•\-\*]\s*(.+)$/gm);
        if (!matches || matches.length === 0) {
          matches = section.match(/^([A-Z][^:\n]+):\s*([^\n]+)/gm);
        }
        if (matches) matches.forEach(match => safetySchools.push(match.replace(/^[•\-\*]\s*/, '').trim()));
      }
    });

    // Parse action plan - Enhanced for various formats
    const actionContent = sections.actionPlan || "";
    const actionPlan = [];
    
    // Try multiple formats for action items
    let actionMatches = actionContent.match(/\*\*([^*]+)\*\*\s*(?:\([^)]+\))?\s*\n([^*]+?)(?=\*\*|$)/gs);
    
    // If no bold matches, try looking for action titles without bold
    if (!actionMatches || actionMatches.length === 0) {
      // Look for action items that start with uppercase and have descriptions
      const actionLines = actionContent.split('\n').filter(line => line.trim());
      let currentTitle = null;
      let currentDesc = [];
      let stepNum = 1;
      
      actionLines.forEach(line => {
        const trimmed = line.trim();
        // Check if this looks like a title (starts with uppercase letter, no bullet, reasonable length)
        if (/^[A-Z][A-Za-z\s]{10,60}$/.test(trimmed) && !trimmed.includes(':') && !trimmed.startsWith('•')) {
          // Save previous action if exists
          if (currentTitle) {
            actionPlan.push({
              step: stepNum++,
              title: currentTitle,
              description: currentDesc.join(' ').trim() || currentTitle,
              timeline: "See formatted report for timeline"
            });
          }
          currentTitle = trimmed;
          currentDesc = [];
        } else if (currentTitle && trimmed && !trimmed.startsWith('#') && !trimmed.includes('Action Plan')) {
          currentDesc.push(trimmed);
        }
      });
      
      // Don't forget the last action
      if (currentTitle) {
        actionPlan.push({
          step: stepNum,
          title: currentTitle,
          description: currentDesc.join(' ').trim() || currentTitle,
          timeline: "See formatted report for timeline"
        });
      }
    } else {
      // Original parsing logic for bold format
      actionMatches.forEach((match, index) => {
        const titleMatch = match.match(/\*\*([^*]+)\*\*/);
        const descMatch = match.replace(/\*\*[^*]+\*\*\s*(?:\([^)]+\))?\s*\n/, '').trim();
        if (titleMatch) {
          actionPlan.push({
            step: index + 1,
            title: titleMatch[1].trim(),
            description: descMatch.split('\n')[0].trim(),
            timeline: "See formatted report for timeline"
          });
        }
      });
    }

    console.log('✅ ConversationalResults: Parsed sections:', Object.keys(sections));
    console.log('📝 Final extracted data:');
    console.log('  - strategicAdvantage:', strategicAdvantageText?.substring(0, 100));
    console.log('  - superpowers count:', superpowers.length);
    console.log('  - essayTopics count:', essayTopics.length);
    console.log('  - colleges - reach:', reachSchools.length, 'target:', targetSchools.length, 'safety:', safetySchools.length);
    console.log('  - actionPlan count:', actionPlan.length);
    
    // Return NEW nested structure (8-section format)
    return {
      studentProfile: {
        name: "Student",
        grade: "Not specified",
        strengths: ["Analytical thinking", "Collaborative leadership", "Problem-solving"],
        interests: ["Academic excellence", "Community impact", "Innovation"],
        goals: "Achieve admission to top universities while staying true to authentic self"
      },
      
      insights: {
        strategicAdvantage: {
          title: "Your Strategic Advantage",
          competitiveHeadline: "The analytical problem-solver who connects systems and people",
          primaryAdvantage: strategicAdvantageText,
          tacticalApplications: {
            essays: "Show your analytical process solving real problems",
            interviews: "Demonstrate how you connect different perspectives",
            activities: "Highlight leadership that brings people together"
          },
          weapons: [
            {
              weapon: "Analytical thinking",
              deployment: "Use in essays about problem-solving",
              impact: "Shows intellectual rigor colleges seek"
            },
            {
              weapon: "Collaborative leadership",
              deployment: "Highlight in activities and recommendations",
              impact: "Demonstrates community building skills"
            },
            {
              weapon: "Systems thinking",
              deployment: "Feature in Why Major essays",
              impact: "Shows ability to see big picture"
            }
          ],
          urgencyLevel: "🔴",
          competitiveDifferentiation: "95% of applicants can't combine analytical depth with collaborative impact"
        },
        
        intellectualSignature: {
          title: intellectualTitle,
          subtitle: "Your unique thinking style and approach",
          content: intellectualContent.split('\n').slice(0, 3).join(' ').trim() || "Your distinctive approach to problem-solving and learning",
          leveragePoints: [
            "For essays: Show your analytical process in action",
            "For interviews: Demonstrate your systematic thinking",
            "For school selection: Find programs that value your approach",
            "For activities: Highlight leadership and problem-solving"
          ],
          strategicAngle: "Your systematic approach to complex challenges",
          deploymentTactics: [
            {
              context: "Academic essays",
              tactic: "Show step-by-step problem-solving approach"
            },
            {
              context: "Research opportunities",
              tactic: "Emphasize interdisciplinary thinking"
            }
          ]
        },
        
        hiddenSuperpowers: {
          title: "Your Secret Weapons",
          subtitle: "The unique strengths that set you apart",
          content: superpowersContent.split('\n').slice(0, 2).join(' ').trim() || "Your distinctive qualities make you valuable to admissions committees",
          superpowers: superpowers.length > 0 ? 
            superpowers.slice(0, 3).map(sp => ({
              power: sp.split(':')[0]?.trim() || sp,
              uniqueValue: "Rare combination others can't replicate",
              applicationStrategy: sp.split(':')[1]?.trim() || "Deploy strategically in essays"
            })) : 
            [
              {
                power: "Analytical-interpersonal bridge",
                uniqueValue: "Rare combination of technical and people skills",
                applicationStrategy: "Show in collaborative problem-solving stories"
              }
            ],
          narrative: "You naturally solve problems by connecting systems and people",
          scarcityFactor: "This combination appears in less than 5% of applicants"
        },
        
        strategicPositioning: {
          title: "How to Stand Out",
          subtitle: "Your competitive advantage",
          brand: "The analytical problem-solver who keeps people centered",
          whyItWorks: "You demonstrate both technical competence and emotional intelligence",
          deployment: [
            {
              component: "Personal Statement",
              strategy: "Show your problem-solving process with human impact"
            },
            {
              component: "Why Major essays",
              strategy: "Connect analytical skills to collaborative goals"
            },
            {
              component: "Why This School essays",
              strategy: "Target programs valuing interdisciplinary thinking"
            },
            {
              component: "Activity descriptions",
              strategy: "Emphasize leadership and community building"
            },
            {
              component: "Interview prep",
              strategy: "Discuss systematic approach to challenges"
            }
          ],
          targetSchools: reachSchools.slice(0, 3).map(school => ({
            school: school.split(':')[0]?.trim() || school,
            whyYouWin: school.split(':')[1]?.trim() || "Perfect fit for your profile"
          })),
          pitch: "You solve complex problems while keeping people at the center"
        },
        
        tacticalAdvantages: {
          title: "Your Tactical Advantages",
          subtitle: "Specific deployment strategies with urgency indicators",
          advantages: [
            {
              advantage: "Bridge analytical and interpersonal skills",
              urgency: "🔴",
              deployment: "Feature prominently in personal statement",
              expectedOutcome: "Immediate differentiation from typical applicants"
            },
            {
              advantage: "Systems thinking approach",
              urgency: "🟡",
              deployment: "Develop through specific examples in supplementals",
              expectedOutcome: "Shows intellectual maturity"
            },
            {
              advantage: "Community impact focus",
              urgency: "🟢",
              deployment: "Build through continued activities",
              expectedOutcome: "Long-term demonstration of values"
            }
          ],
          executionPriorities: [
            "🔴 Immediate: Draft personal statement highlighting bridge-building",
            "🟡 Short-term: Gather specific examples for essays",
            "🟢 Strategic: Deepen leadership roles in activities"
          ]
        },
        
        essayStrategy: {
          title: "Essay Game Plan",
          subtitle: "Your narrative strategy",
          goldStory: {
            title: "Your Best Story",
            story: "Use examples that show your analytical skills in action with real impact",
            strategicFraming: "Frame challenges as opportunities to demonstrate your problem-solving approach",
            competitiveAngle: "Shows depth most applicants can't match"
          },
          essayTopics: essayTopics.length > 0 ? 
            essayTopics.slice(0, 3).map(topic => ({
              topic: topic,
              whyItWorks: "Showcases your unique combination of skills",
              differentiator: "Goes beyond typical applicant stories"
            })) :
            [
              {
                topic: "Your systematic approach to complex challenges",
                whyItWorks: "Shows intellectual rigor",
                differentiator: "Demonstrates analytical depth"
              },
              {
                topic: "Times you've helped others navigate difficult situations",
                whyItWorks: "Shows leadership and empathy",
                differentiator: "Combines technical and human elements"
              }
            ],
          avoidTraps: ["Don't just describe problems - show your solutions", "Avoid generic 'helping people' themes without specific examples"],
          theme: "The strategic thinker who creates practical solutions"
        },
        
        collegeRecommendations: {
          title: "College Recommendations",
          subtitle: "Schools that match your profile",
          reach: reachSchools.slice(0, 2).map(school => ({
            school: school.split(':')[0]?.trim() || school,
            whyYouMatch: "Values your analytical and collaborative approach",
            competitiveEdge: school.split(':')[1]?.trim() || "Your profile aligns with their priorities"
          })),
          target: targetSchools.slice(0, 2).map(school => ({
            school: school.split(':')[0]?.trim() || school,
            whyYouMatch: "Perfect fit for your balanced profile",
            competitiveEdge: school.split(':')[1]?.trim() || "You'd excel in their environment"
          })),
          safety: safetySchools.slice(0, 2).map(school => ({
            school: school.split(':')[0]?.trim() || school,
            whyYouMatch: "Strong foundation for your goals",
            competitiveEdge: school.split(':')[1]?.trim() || "You'd be a standout student"
          }))
        },
        
        actionPlan: actionPlan.length > 0 ? 
          actionPlan.map((item, idx) => ({
            ...item,
            urgency: idx < 2 ? "🔴" : idx < 4 ? "🟡" : "🟢",
            impact: idx < 2 ? "Critical for immediate progress" : idx < 4 ? "Important for building momentum" : "Strategic long-term positioning"
          })) :
          [
            {
              step: 1,
              urgency: "🔴",
              title: "Review Your Complete Assessment",
              description: "Study the detailed formatting and recommendations below",
              timeline: "This week",
              impact: "Foundation for all application work"
            }
          ],
        
        successMetrics: {
          title: "Your Success Metrics",
          subtitle: "Measurable milestones that build confidence",
          immediateWins: [
            {
              metric: "Complete personal statement draft",
              measurement: "First draft showing your bridge-building approach",
              timeline: "This week"
            },
            {
              metric: "Identify 3 specific stories",
              measurement: "Examples that showcase your advantages",
              timeline: "Within 2 weeks"
            }
          ],
          monthlyMilestones: [
            {
              metric: "Complete 5 supplemental essays",
              measurement: "Each highlighting different strengths",
              expectedOutcome: "Strong application portfolio"
            },
            {
              metric: "Secure 2 strong recommendations",
              measurement: "Teachers who can speak to your abilities",
              expectedOutcome: "Third-party validation of strengths"
            }
          ],
          semesterGoals: [
            {
              metric: "Submit all applications",
              measurement: "Complete, polished, strategic submissions",
              competitivePosition: "Top 10% of applicant pool"
            },
            {
              metric: "Deepen leadership roles",
              measurement: "Measurable impact in 2+ activities",
              competitivePosition: "Demonstrated sustained commitment"
            }
          ],
          confidenceBuilders: [
            "Your unique combination is genuinely rare",
            "Your approach solves real problems colleges face",
            "Your profile fills specific institutional needs"
          ]
        }
      }
    }; 
  };

  // Helper function to clean and format text with proper structure
  const formatText = (text) => {
    if (!text) return null;
    
    // First, clean up markdown and formatting issues
    let cleanedText = text
      .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold markdown
      .replace(/\*([^*]+)\*/g, '$1')     // Remove italic markdown  
      .replace(/`([^`]+)`/g, '$1')       // Remove code formatting
      .replace(/\*\s*$/, '')             // Remove trailing asterisks
      .trim();
    
    return formatAdvancedText(cleanedText);
  };

  const formatAdvancedText = (text) => {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    const elements = [];
    let i = 0;
    
    while (i < lines.length) {
      const line = lines[i];
      
      // Skip intro metadata like "From your conversation responses"
      if (line.toLowerCase().includes('from your conversation') || 
          line.toLowerCase().includes('from your responses')) {
        i++;
        continue;
      }
      
      // Check for headers
      if (isAdvancedHeader(line)) {
        elements.push(
          <h4 key={`header-${i}`} className="text-lg font-semibold text-slate-900 mt-6 mb-3 first:mt-0">
            {cleanHeader(line)}
          </h4>
        );
        i++;
        continue;
      }
      
      // Check for numbered items like "(1) His approach..." 
      if (/^\(\d+\)/.test(line) || /^\d+\./.test(line)) {
        const numberedItems = [];
        while (i < lines.length && (/^\(\d+\)/.test(lines[i]) || /^\d+\./.test(lines[i]))) {
          numberedItems.push(lines[i].replace(/^\(\d+\)\s*/, '').replace(/^\d+\.\s*/, ''));
          i++;
        }
        
        elements.push(
          <ol key={`numbered-${i}`} className="list-decimal list-inside space-y-2 mb-4 text-slate-900 leading-relaxed pl-4">
            {numberedItems.map((item, idx) => (
              <li key={idx} className="ml-2">{item}</li>
            ))}
          </ol>
        );
        continue;
      }
      
      // Check for bullet points
      if (line.startsWith('- ') || line.startsWith('• ')) {
        const bulletItems = [];
        while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('• '))) {
          bulletItems.push(lines[i].replace(/^[-•]\s*/, ''));
          i++;
        }
        
        elements.push(
          <ul key={`bullets-${i}`} className="list-disc list-inside space-y-2 mb-4 text-slate-900 leading-relaxed pl-4">
            {bulletItems.map((item, idx) => (
              <li key={idx} className="ml-2">{item}</li>
            ))}
          </ul>
        );
        continue;
      }
      
      // Regular paragraph - collect consecutive non-header lines
      const paragraphLines = [];
      while (i < lines.length && !isAdvancedHeader(lines[i]) && 
             !lines[i].startsWith('- ') && !lines[i].startsWith('• ') && 
             !/^\(\d+\)/.test(lines[i]) && !/^\d+\./.test(lines[i])) {
        paragraphLines.push(lines[i]);
        i++;
      }
      
      if (paragraphLines.length > 0) {
        const paragraphText = paragraphLines.join(' ');
        if (paragraphText.trim()) {
          elements.push(
            <p key={`para-${i}`} className="text-slate-900 leading-relaxed mb-4">
              {paragraphText}
            </p>
          );
        }
      }
    }
    
    return elements;
  };

  const isAdvancedHeader = (line) => {
    const clean = line.replace(/[\*:`]/g, '').trim();
    
    return (
      // ALL CAPS sections
      /^[A-Z\s]{15,}$/.test(clean) ||
      // Ends with colon
      line.endsWith(':') ||
      // Bold markdown
      /^\*\*.*\*\*:?$/.test(line) ||
      // Specific patterns
      /^(REACH TIER|MATCH TIER|SAFETY TIER|STRATEGIC|IMMEDIATE)/i.test(clean) ||
      // Long capitalized phrases
      (clean.length > 20 && /^[A-Z][A-Z\s&]+$/.test(clean))
    );
  };

  const formatSection = (section, index) => {
    const lines = section.split('\n').filter(line => line.trim());
    if (!lines.length) return null;
    
    const elements = [];
    let currentParagraph = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check if this looks like a header
      if (isHeader(line)) {
        // Flush current paragraph
        if (currentParagraph.length > 0) {
          elements.push(formatParagraph(currentParagraph.join(' '), elements.length));
          currentParagraph = [];
        }
        
        // Add header
        elements.push(
          <h4 key={`header-${index}-${i}`} className="text-lg font-semibold text-slate-900 mt-6 mb-3 first:mt-0">
            {cleanHeader(line)}
          </h4>
        );
      } else if (line.startsWith('- ') || line.startsWith('• ')) {
        // Handle bullet points
        if (currentParagraph.length > 0) {
          elements.push(formatParagraph(currentParagraph.join(' '), elements.length));
          currentParagraph = [];
        }
        
        // Collect consecutive bullet points
        const bullets = [line];
        while (i + 1 < lines.length && (lines[i + 1].startsWith('- ') || lines[i + 1].startsWith('• '))) {
          i++;
          bullets.push(lines[i].trim());
        }
        
        elements.push(
          <ul key={`bullets-${index}-${i}`} className="list-disc list-inside space-y-1 mb-4 text-slate-900 leading-relaxed">
            {bullets.map((bullet, bIndex) => (
              <li key={bIndex} className="ml-2">
                {bullet.replace(/^[-•]\s*/, '')}
              </li>
            ))}
          </ul>
        );
      } else if (line) {
        currentParagraph.push(line);
      }
    }
    
    // Flush remaining paragraph
    if (currentParagraph.length > 0) {
      elements.push(formatParagraph(currentParagraph.join(' '), elements.length));
    }
    
    return elements;
  };

  const isHeader = (line) => {
    // Remove common formatting
    const clean = line.replace(/[\*:`]/g, '').trim();
    
    // Check various header patterns
    return (
      /^[A-Z][^a-z]{10,}$/.test(clean) ||  // ALL CAPS
      /^[A-Z][^.!?]{20,}$/.test(clean) ||  // Long capitalized line
      line.endsWith(':') ||                 // Ends with colon
      /^\*\*.*\*\*:?$/.test(line)          // Bold markdown pattern
    );
  };

  const cleanHeader = (line) => {
    return line
      .replace(/[\*:`]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const formatParagraph = (text, index) => {
    if (!text.trim()) return null;
    
    return (
      <p key={`para-${index}`} className="text-slate-900 leading-relaxed mb-4">
        {text.trim()}
      </p>
    );
  };

  // Load student profile to get grade level
  const loadStudentProfile = async () => {
    if (!currentUser?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('student_academic_profiles')
        .select('grade_level')
        .eq('user_id', currentUser.id)
        .single();
      
      if (data?.grade_level) {
        setStudentGradeLevel(data.grade_level);
        console.log('✅ Loaded student grade level:', data.grade_level);
      }
    } catch (error) {
      console.error('Error loading student profile:', error);
    }
  };


  const loadInsights = async () => {
    // Prevent repeated validation calls
    if (isValidating) {
      console.log('🔍 ConversationalResults: Already validating, skipping...');
      return;
    }
    
    setIsValidating(true);
    
    // Helper function to reset validation state
    const finishValidation = () => {
      setIsValidating(false);
    };
    
    console.log('🔍 ConversationalResults: Loading insights from database', {
      currentUser: currentUser?.name || currentUser?.displayName,
      userId: currentUser?.id
    });
    
    setError(null);
    
    // Add a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.log('⏰ ConversationalResults: Loading timeout, forcing completion');
      setIsLoading(false);
      finishValidation();
    }, 8000); // 8 second timeout
    
    // First, try to load enhanced assessment insights from assessment_responses table
    try {
      console.log('🔍 ConversationalResults: Querying for assessment with user_id:', currentUser?.id);
      
      // Query for assessments with formatted_insights (both 'enhanced' and 'framework_b' types)
      const { data: assessmentData, error: assessmentError } = await supabase
        .from('assessment_responses')
        .select('insights, formatted_insights, assessment_type, completed_at, parent_can_view, shared_at, shared_with_notification')
        .eq('user_id', currentUser?.id)
        .in('assessment_type', ['enhanced', 'framework_b'])  // Look for both types
        .not('formatted_insights', 'is', null)  // Must have formatted insights
        .order('completed_at', { ascending: false })
        .limit(1);
      
      console.log('📊 ConversationalResults: Query result:', { 
        hasData: !!assessmentData, 
        length: assessmentData?.length,
        error: assessmentError 
      });
      
      if (assessmentError) {
        console.error('❌ ConversationalResults: Error loading enhanced assessment:', assessmentError);
        
        // Check if this is an authentication error that might cause redirect
        if (assessmentError.code === 'PGRST301' || assessmentError.message?.includes('JWT') || assessmentError.message?.includes('auth')) {
          console.warn('⚠️ Authentication error in loadInsights - proceeding with fallback data');
          // Don't throw or return early - proceed to fallback data
        }
      } else if (assessmentData && assessmentData.length > 0) {
        console.log('✅ ConversationalResults: Found enhanced assessment insights');
        console.log('   Assessment type:', assessmentData[0].assessment_type);
        console.log('   Has formatted_insights:', !!assessmentData[0].formatted_insights);
        console.log('   Has insights:', !!assessmentData[0].insights);
        
        const assessment = assessmentData[0];
        // Prefer formatted_insights (structured JSON) over insights (raw markdown/text)
        const enhancedInsights = assessment.formatted_insights || assessment.insights;
        
        // Load sharing permissions from the assessment data
        setSharingPermissions({
          parentCanView: assessment.parent_can_view || false,
          sharedAt: assessment.shared_at,
          notifyParent: assessment.shared_with_notification !== false // Default to true if not set
        });
        
        // First check if we have valid insights data
        if (enhancedInsights) {
          // Log the structure for debugging
          console.log('📊 ConversationalResults: Enhanced insights structure:', {
            type: typeof enhancedInsights,
            hasInsights: !!enhancedInsights.insights,
            hasStrategicAdvantage: !!enhancedInsights.insights?.strategicAdvantage,
            keys: Object.keys(enhancedInsights || {})
          });
          
          // Check if insights are already in structured JSON format
          let structuredInsights;
          
          // Handle formatted_insights from database with { insights: {...} } structure
          if (typeof enhancedInsights === 'object' && enhancedInsights.insights?.strategicAdvantage) {
            console.log('✅ ConversationalResults: Using structured JSON insights from database');
            // The formatted_insights from database already has the correct structure
            // It contains { insights: { strategicAdvantage, hiddenSuperpowers, etc. } }
            structuredInsights = enhancedInsights;  // Use as-is, it's already properly structured
            
            // Since we have valid structured data, proceed without strict validation
            setInsights(structuredInsights);
            setDataSource('enhanced_assessment_new_format');
            clearTimeout(timeoutId);
            setIsLoading(false);
            finishValidation();
            return;
          } else if (typeof enhancedInsights === 'object' && enhancedInsights.strategicAdvantage) {
            console.log('✅ ConversationalResults: Wrapping flat JSON insights');
            // This is the older format where insights are at the top level
            structuredInsights = {
              insights: enhancedInsights,  // Wrap in insights object
              studentProfile: {
                name: currentUser?.displayName || currentUser?.name || "Student",
                grade: currentUser?.grade || "Not specified",
                strengths: ["Analytical thinking", "Collaborative leadership", "Problem-solving"],
                interests: ["Academic excellence", "Community impact", "Innovation"],
                goals: "Achieve admission to top universities while staying true to authentic self"
              }
            };
            
            setInsights(structuredInsights);
            setDataSource('enhanced_assessment_new_format');
            clearTimeout(timeoutId);
            setIsLoading(false);
            finishValidation();
            return;
          } else if (typeof enhancedInsights === 'string') {
            console.log('✅ ConversationalResults: Parsing markdown insights to structured format');
            structuredInsights = parseMarkdownToStructuredInsights(enhancedInsights);
            
            if (structuredInsights) {
              setInsights(structuredInsights);
              setDataSource('enhanced_assessment_new_format');
              clearTimeout(timeoutId);
              setIsLoading(false);
              finishValidation();
              return;
            }
          } else {
            console.log('⚠️ ConversationalResults: Unknown insights format, validation result:', validateEnhancedInsightsData(enhancedInsights));
          }
        } else {
          console.log('⚠️ ConversationalResults: No enhanced insights found');
        }
      } else {
        console.log('🔍 ConversationalResults: No enhanced assessment found, using fallback data');
      }
    } catch (error) {
      console.error('❌ ConversationalResults: Error checking enhanced assessment:', error);
      
      // Check if this is an authentication error - if so, continue with fallback
      if (error.message?.includes('JWT') || error.message?.includes('auth') || error.message?.includes('session')) {
        console.warn('⚠️ Authentication error caught in loadInsights - proceeding with fallback data');
      }
      // Don't re-throw - proceed to fallback data
    }
    
    
    
    // Fallback insights for demo purposes (strategic format)
    console.log('🔄 ConversationalResults: Setting fallback strategic insights');
    setInsights({
      studentProfile: {
        name: currentUser?.displayName || currentUser?.name || "Student",
        grade: studentGradeLevel || "Not specified",
        strengths: ["Analytical thinking", "Collaborative leadership", "Problem-solving"],
        interests: ["Academic excellence", "Community impact", "Innovation"],
        goals: "Achieve admission to top universities while staying true to authentic self"
      },
      
      insights: {
        strategicAdvantage: {
          title: "Your Strategic Advantage",
          competitiveHeadline: "The analytical problem-solver who connects systems and people",
          primaryAdvantage: "You combine analytical thinking with collaborative leadership in a way that makes you stand out from typical applicants. Your approach to challenges shows both logical reasoning and genuine care for bringing people together to solve problems.",
          tacticalApplications: {
            essays: "Show your analytical process solving real problems",
            interviews: "Demonstrate how you connect different perspectives",
            activities: "Highlight leadership that brings people together"
          },
          weapons: [
            {
              weapon: "Analytical thinking",
              deployment: "Use in essays about problem-solving",
              impact: "Shows intellectual rigor colleges seek"
            },
            {
              weapon: "Collaborative leadership",
              deployment: "Highlight in activities and recommendations",
              impact: "Demonstrates community building skills"
            },
            {
              weapon: "Systems thinking",
              deployment: "Feature in Why Major essays",
              impact: "Shows ability to see big picture"
            }
          ],
          urgencyLevel: "🔴",
          competitiveDifferentiation: "95% of applicants can't combine analytical depth with collaborative impact"
        },
        intellectualSignature: {
          title: "How Your Brain Works",
          subtitle: "Your collaborative problem-solving approach",
          content: "You naturally break down complex problems while simultaneously thinking about how to bring the right people together to solve them. This bridge-building mindset makes you valuable in academic and professional settings.",
          leveragePoints: [
            "For essays: Show how you solve problems by connecting different perspectives",
            "For interviews: Share specific examples of bringing people together for solutions",
            "For choosing schools: Look for programs that value collaboration and interdisciplinary thinking",
            "For picking majors: Consider fields that combine technical skills with human impact"
          ],
          strategicAngle: "I'm someone who sees solutions by connecting people and ideas",
          deploymentTactics: [
            {
              context: "Academic essays",
              tactic: "Show step-by-step problem-solving approach"
            },
            {
              context: "Research opportunities",
              tactic: "Emphasize interdisciplinary thinking"
            }
          ]
        },
        hiddenSuperpowers: {
          title: "Your Secret Weapons",
          subtitle: "The unique strengths that set you apart from other applicants",
          content: "What you might see as just 'being collaborative' are actually rare leadership abilities that colleges actively seek in their student body.",
          superpowers: [
            {
              power: "Bridge-building",
              uniqueValue: "Helps diverse groups work together effectively",
              applicationStrategy: "Feature in leadership essays"
            },
            {
              power: "Systems thinking",
              uniqueValue: "Understand how people and processes connect",
              applicationStrategy: "Highlight in academic essays"
            },
            {
              power: "Natural facilitation",
              uniqueValue: "Make others feel heard and valued",
              applicationStrategy: "Show in community service stories"
            }
          ],
          narrative: "I naturally bring people together to create solutions that actually work",
          scarcityFactor: "This combination appears in less than 5% of applicants"
        },
        strategicPositioning: {
          title: "How to Stand Out",
          subtitle: "What makes you different from other applicants",
          brand: "The collaborative leader who solves complex problems by connecting people and ideas",
          whyItWorks: "Most applicants focus on individual achievements - you show how you amplify others",
          deployment: [
            {
              component: "Personal Statement",
              strategy: "Tell a story that shows your collaborative leadership in action"
            },
            {
              component: "Why Major essays",
              strategy: "Connect your people-centered approach to your field of study"
            },
            {
              component: "Why This School essays",
              strategy: "Research programs that value collaboration and community impact"
            },
            {
              component: "Activity descriptions",
              strategy: "Highlight times you brought different groups together"
            },
            {
              component: "Interview prep",
              strategy: "Practice explaining how you create inclusive solutions"
            }
          ],
          targetSchools: [
            {
              school: "Schools with collaborative environments",
              whyYouWin: "Your approach matches their values perfectly"
            },
            {
              school: "Universities with community engagement",
              whyYouWin: "You embody their mission of social impact"
            },
            {
              school: "Colleges valuing student leadership",
              whyYouWin: "You bring the leadership they seek"
            }
          ],
          pitch: "I'm the person who gets things done by bringing out the best in others"
        },
        
        tacticalAdvantages: {
          title: "Your Tactical Advantages",
          subtitle: "Specific deployment strategies with urgency indicators",
          advantages: [
            {
              advantage: "Bridge analytical and interpersonal skills",
              urgency: "🔴",
              deployment: "Feature prominently in personal statement",
              expectedOutcome: "Immediate differentiation from typical applicants"
            },
            {
              advantage: "Systems thinking approach",
              urgency: "🟡",
              deployment: "Develop through specific examples in supplementals",
              expectedOutcome: "Shows intellectual maturity"
            },
            {
              advantage: "Community impact focus",
              urgency: "🟢",
              deployment: "Build through continued activities",
              expectedOutcome: "Long-term demonstration of values"
            }
          ],
          executionPriorities: [
            "🔴 Immediate: Draft personal statement highlighting bridge-building",
            "🟡 Short-term: Gather specific examples for essays",
            "🟢 Strategic: Deepen leadership roles in activities"
          ]
        },
        essayStrategy: {
          title: "Essay Game Plan",
          subtitle: "How to turn your stories into essays that actually get you in",
          goldStory: {
            title: "Your Best Story",
            story: "A time when you brought different people together to solve a problem that mattered",
            strategicFraming: "Show how your collaborative approach creates better outcomes than individual effort",
            competitiveAngle: "Shows depth most applicants can't match"
          },
          essayTopics: [
            {
              topic: "The project that almost failed until you figured out how to get everyone working together",
              whyItWorks: "Shows problem-solving under pressure",
              differentiator: "Demonstrates resilience and leadership"
            },
            {
              topic: "When listening to different perspectives led to a breakthrough solution",
              whyItWorks: "Shows intellectual humility and growth",
              differentiator: "Proves you value diverse thinking"
            },
            {
              topic: "A time you helped resolve conflict between groups",
              whyItWorks: "Shows mediation and diplomacy skills",
              differentiator: "Demonstrates mature conflict resolution"
            }
          ],
          avoidTraps: [
            "Don't just say you're collaborative - show specific results you created",
            "Avoid generic teamwork stories unless they show your unique facilitation style"
          ],
          theme: "The bridge-builder who creates solutions by connecting people and ideas"
        },
        collegeRecommendations: {
          title: "College Recommendations",
          subtitle: "Schools that would be perfect for someone like you",
          reach: [
            {
              school: "Stanford",
              whyYouMatch: "Values collaborative innovation and interdisciplinary problem-solving",
              competitiveEdge: "Your bridge-building approach fits their culture"
            },
            {
              school: "Brown",
              whyYouMatch: "Open curriculum allows you to connect different fields of study",
              competitiveEdge: "Your interdisciplinary thinking thrives here"
            }
          ],
          target: [
            {
              school: "Northeastern",
              whyYouMatch: "Co-op program lets you apply collaborative skills in real settings",
              competitiveEdge: "Your practical approach aligns with their model"
            },
            {
              school: "University of Rochester",
              whyYouMatch: "Encourages interdisciplinary study and student leadership",
              competitiveEdge: "Your leadership style matches their values"
            }
          ],
          safety: [
            {
              school: "Virginia Tech",
              whyYouMatch: "Collaborative engineering culture with strong community focus",
              competitiveEdge: "You'd be a standout in their programs"
            },
            {
              school: "University of Vermont",
              whyYouMatch: "Values sustainability and community engagement",
              competitiveEdge: "Your values align perfectly with theirs"
            }
          ]
        },
        actionPlan: [
          {
            step: 1,
            urgency: "🔴",
            title: "Document Your Bridge-Building Examples",
            description: "Write down specific times when you brought people together to solve problems",
            timeline: "This week",
            impact: "Foundation for all essays"
          },
          {
            step: 2,
            urgency: "🔴",
            title: "Research Collaborative Programs",
            description: "Find schools and majors that explicitly value teamwork and interdisciplinary thinking",
            timeline: "Next 2 weeks",
            impact: "Strategic school selection"
          },
          {
            step: 3,
            urgency: "🟡",
            title: "Draft Your Collaborative Leadership Essays",
            description: "Start with your best story showing how you create inclusive solutions",
            timeline: "Month 1",
            impact: "Core application narrative"
          },
          {
            step: 4,
            urgency: "🟡",
            title: "Connect With Current Students",
            description: "Reach out to students at target schools to learn about collaborative opportunities",
            timeline: "Month 2",
            impact: "Insider knowledge for essays"
          },
          {
            step: 5,
            urgency: "🟢",
            title: "Refine Your Leadership Narrative",
            description: "Polish how you describe your collaborative approach across all materials",
            timeline: "Month 3",
            impact: "Consistent powerful messaging"
          },
          {
            step: 6,
            urgency: "🟢",
            title: "Practice Your Story",
            description: "Rehearse explaining your collaborative leadership with concrete examples",
            timeline: "Before applications",
            impact: "Interview preparation"
          }
        ],
        
        successMetrics: {
          title: "Your Success Metrics",
          subtitle: "Measurable milestones that build confidence",
          immediateWins: [
            {
              metric: "Complete personal statement draft",
              measurement: "First draft showing your bridge-building approach",
              timeline: "This week"
            },
            {
              metric: "Identify 3 specific stories",
              measurement: "Examples that showcase your advantages",
              timeline: "Within 2 weeks"
            }
          ],
          monthlyMilestones: [
            {
              metric: "Complete 5 supplemental essays",
              measurement: "Each highlighting different strengths",
              expectedOutcome: "Strong application portfolio"
            },
            {
              metric: "Secure 2 strong recommendations",
              measurement: "Teachers who can speak to your abilities",
              expectedOutcome: "Third-party validation of strengths"
            }
          ],
          semesterGoals: [
            {
              metric: "Submit all applications",
              measurement: "Complete, polished, strategic submissions",
              competitivePosition: "Top 10% of applicant pool"
            },
            {
              metric: "Deepen leadership roles",
              measurement: "Measurable impact in 2+ activities",
              competitivePosition: "Demonstrated sustained commitment"
            }
          ],
          confidenceBuilders: [
            "Your unique combination is genuinely rare",
            "Your approach solves real problems colleges face",
            "Your profile fills specific institutional needs"
          ]
        }
      }
    });
      clearTimeout(timeoutId);
      setIsLoading(false);
      finishValidation();
  };

  // Save results permanently to database with HTML format
  const saveResultsPermanently = async () => {
    if (!insights || !currentUser || isSavingResults) return;
    
    setIsSavingResults(true);
    console.log('💾 Saving formatted results permanently...');
    
    try {
      // Check if user is still authenticated before attempting save
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.warn('⚠️ User session invalid, skipping permanent save to avoid auth redirect');
        setIsSavingResults(false);
        return;
      }
      
      // Generate HTML version of results
      const formattedHtml = renderResultsToHtml(insights, currentUser, studentGradeLevel);
      
      // Save to database with additional error handling
      const savedResults = await saveFormattedResults(currentUser.id, insights, formattedHtml);
      
      if (savedResults?.results_permalink) {
        setResultsPermalink(savedResults.results_permalink);
        setSavedSuccessfully(true);
        console.log('✅ Results saved permanently with permalink:', savedResults.results_permalink);
        
        // REMOVED: Automatic strategic summary generation to prevent concurrent API call issues
        // Users can manually generate strategic summaries from their Student Profile Hub
        // This prevents timeout errors and gives users control over when to generate summaries
        
        // Navigate to results page after a short delay
        setTimeout(() => {
          console.log('📍 Assessment complete! You can generate a strategic summary from your profile page.');
          // Navigate to the saved results page instead of strategic profile
          if (savedResults?.results_permalink) {
            navigate(`/college/strategic-profile/${savedResults.results_permalink}`);
          }
        }, 2000); // Small delay to allow user to see the success message
        
        /* COMMENTED OUT: Automatic strategic summary generation
        try {
          console.log('🤖 Generating strategic summary automatically after assessment...');
          await StrategicSummaryService.generateStrategicSummary(currentUser.id);
          console.log('✅ Strategic summary generated automatically');
          
          // Optional: Navigate to strategic profile after generation
          setTimeout(() => {
            console.log('📍 Navigating to Strategic Profile page...');
            navigate('/college/strategic-profile');
          }, 2000); // Small delay to allow user to see the success message
          
        } catch (summaryError) {
          console.error('❌ Failed to generate strategic summary automatically:', summaryError);
          // Don't let this error break the main flow - just log it
        }
        */
      } else {
        console.warn('⚠️ Results save returned no permalink, but no error thrown');
      }
    } catch (error) {
      console.error('❌ Failed to save results permanently:', error);
      // Don't re-throw the error to prevent navigation issues
    } finally {
      setIsSavingResults(false);
    }
  };

  const saveInsights = () => {
    const insightsData = {
      studentName: currentUser?.displayName || currentUser?.name || 'Student',
      completedAt: new Date().toISOString(),
      insights,
      version: 'conversational-v1'
    };
    
    const dataStr = JSON.stringify(insightsData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `college-insights-${currentUser?.name || 'student'}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const startCollegeResearch = () => {
    navigate('/college/research');
  };

  const exploreEssayIdeas = () => {
    navigate('/college/essays');
  };

  const findOpportunities = () => {
    navigate('/college/opportunities');
  };

  const goToDashboard = () => {
    navigate('/college');
  };

  // Integration functions
  const parseCollegeName = (schoolText) => {
    // Extract college name from text like "Northwestern: Co-op program..." -> "Northwestern"
    const colonIndex = schoolText.indexOf(':');
    return colonIndex > 0 ? schoolText.substring(0, colonIndex).trim() : schoolText.trim();
  };

  const addCollegeToList = async (schoolText, category) => {
    if (!addCollege || isIntegrating) return;
    
    setIsIntegrating(true);
    
    try {
      const collegeName = parseCollegeName(schoolText);
      const collegeKey = `${category}-${collegeName}`;
      
      // Check if already added
      if (integrationStatus.colleges[collegeKey]) {
        console.log('College already added:', collegeName);
        return;
      }
      
      const collegeData = {
        name: collegeName,
        category: category === 'reach' ? 'reach' : category, // Map reach -> reach for existing system
        notes: schoolText.includes(':') ? schoolText.substring(schoolText.indexOf(':') + 1).trim() : '',
        source: 'strategic_assessment'
      };
      
      console.log('Adding college to list:', collegeData);
      const newCollege = await addCollege(collegeData);
      
      if (newCollege) {
        setIntegrationStatus(prev => ({
          ...prev,
          colleges: {
            ...prev.colleges,
            [collegeKey]: { added: true, id: newCollege.id, name: collegeName }
          }
        }));
        
        console.log('✅ College added successfully:', collegeName);
      }
    } catch (error) {
      console.error('❌ Failed to add college:', error);
    } finally {
      setIsIntegrating(false);
    }
  };


  const addActionToTimeline = async (action) => {
    if (!addTimelineItem || isIntegrating) return;
    
    setIsIntegrating(true);
    
    try {
      const actionKey = `step-${action.step}`;
      
      // Check if already added
      if (integrationStatus.timeline[actionKey]) {
        console.log('Action already added:', action.title);
        return;
      }
      
      // Parse timeline text to actual dates
      const parseTimelineToDate = (timeline) => {
        const now = new Date();
        const timelineLower = timeline.toLowerCase();
        
        if (timelineLower.includes('this week')) {
          const nextWeek = new Date(now);
          nextWeek.setDate(now.getDate() + 7);
          return nextWeek.toISOString().split('T')[0];
        } else if (timelineLower.includes('next week')) {
          const twoWeeks = new Date(now);
          twoWeeks.setDate(now.getDate() + 14);
          return twoWeeks.toISOString().split('T')[0];
        } else if (timelineLower.includes('month 1')) {
          const nextMonth = new Date(now);
          nextMonth.setMonth(now.getMonth() + 1);
          return nextMonth.toISOString().split('T')[0];
        } else if (timelineLower.includes('month 2')) {
          const twoMonths = new Date(now);
          twoMonths.setMonth(now.getMonth() + 2);
          return twoMonths.toISOString().split('T')[0];
        } else if (timelineLower.includes('month 3')) {
          const threeMonths = new Date(now);
          threeMonths.setMonth(now.getMonth() + 3);
          return threeMonths.toISOString().split('T')[0];
        } else {
          // Default to 1 week from now
          const oneWeek = new Date(now);
          oneWeek.setDate(now.getDate() + 7);
          return oneWeek.toISOString().split('T')[0];
        }
      };
      
      const timelineData = {
        title: action.title,
        description: action.description,
        due_date: parseTimelineToDate(action.timeline),
        category: 'applications', // Default category
        isPersonal: true,
        source: 'strategic_assessment'
      };
      
      console.log('Adding action to timeline:', timelineData);
      const newTimelineItem = await addTimelineItem(timelineData);
      
      if (newTimelineItem) {
        setIntegrationStatus(prev => ({
          ...prev,
          timeline: {
            ...prev.timeline,
            [actionKey]: { added: true, id: newTimelineItem.id, title: action.title }
          }
        }));
        
        console.log('✅ Action added to timeline successfully:', action.title);
      }
    } catch (error) {
      console.error('❌ Failed to add action to timeline:', error);
    } finally {
      setIsIntegrating(false);
    }
  };

  // Handle sharing results with parents
  const handleShareWithParent = async () => {
    try {
      // Check if user is still authenticated before attempting update
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.warn('⚠️ User session invalid, cannot share results');
        alert('Your session has expired. Please refresh the page and try again.');
        return;
      }
      
      // Update assessment_responses table to mark as shared
      const { error } = await supabase
        .from('assessment_responses')
        .update({
          parent_can_view: true,
          shared_at: new Date().toISOString(),
          shared_with_notification: sharingPermissions.notifyParent
        })
        .eq('user_id', currentUser?.id)
        .eq('assessment_type', 'enhanced');

      if (error) {
        console.error('Error updating sharing permissions:', error);
        
        // Check if this is an authentication error
        if (error.code === 'PGRST301' || error.message?.includes('JWT') || error.message?.includes('auth')) {
          alert('Your session has expired. Please refresh the page and try again.');
        }
        return;
      }

      setSharingPermissions({
        parentCanView: true,
        sharedAt: new Date().toISOString(),
        notifyParent: sharingPermissions.notifyParent
      });
      
      setShowSharingModal(false);
      
      // Optional: Show success message
      console.log('✅ Results shared with parent successfully');
      
    } catch (error) {
      console.error('Error sharing results:', error);
      
      // Check if this is an authentication error
      if (error.message?.includes('JWT') || error.message?.includes('auth') || error.message?.includes('session')) {
        alert('Your session has expired. Please refresh the page and try again.');
      }
    }
  };

  const handleStopSharing = async () => {
    try {
      // Check if user is still authenticated before attempting update
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.warn('⚠️ User session invalid, cannot stop sharing');
        alert('Your session has expired. Please refresh the page and try again.');
        return;
      }
      
      const { error } = await supabase
        .from('assessment_responses')
        .update({
          parent_can_view: false,
          shared_at: null,
          shared_with_notification: false
        })
        .eq('user_id', currentUser?.id)
        .eq('assessment_type', 'enhanced');

      if (error) {
        console.error('Error updating sharing permissions:', error);
        
        // Check if this is an authentication error
        if (error.code === 'PGRST301' || error.message?.includes('JWT') || error.message?.includes('auth')) {
          alert('Your session has expired. Please refresh the page and try again.');
        }
        return;
      }

      setSharingPermissions({
        parentCanView: false,
        sharedAt: null,
        notifyParent: true
      });
      
      console.log('✅ Sharing stopped successfully');
      
    } catch (error) {
      console.error('Error stopping sharing:', error);
      
      // Check if this is an authentication error
      if (error.message?.includes('JWT') || error.message?.includes('auth') || error.message?.includes('session')) {
        alert('Your session has expired. Please refresh the page and try again.');
      }
    }
  };

  if (showCompletion) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center" style={{
        background: 'linear-gradient(135deg, #fafaf9 0%, #f0fdfa 30%, #fef2f7 100%)'
      }}>
        <div className="completion-animation text-6xl mb-6" style={{
          animation: 'gentle-bounce 1.5s ease-out'
        }}>
          ✨
        </div>
        
        <div className="bg-white bg-opacity-90 backdrop-blur-xl border border-stone-200 rounded-3xl p-12 max-w-md mx-auto shadow-2xl">
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 py-3 rounded-full text-sm font-bold inline-block mb-6 uppercase tracking-wider">
            Assessment Complete
          </div>
          
          <h1 className="text-3xl font-bold mb-4 text-slate-900">
            Your Strategic Profile
          </h1>
          
          <p className="text-lg text-stone-500 mb-8">
            Here's what makes you unique and how to leverage it for college admissions
          </p>
          
          <button
            onClick={() => setShowCompletion(false)}
            className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all transform hover:-translate-y-1 shadow-lg hover:shadow-xl"
          >
            See Your Strategic Advantage →
          </button>
        </div>
        
        <style jsx>{`
          @keyframes gentle-bounce {
            0% {
              transform: scale(0.3) translateY(20px);
              opacity: 0;
            }
            50% {
              transform: scale(1.05) translateY(-10px);
              opacity: 1;
            }
            100% {
              transform: scale(1) translateY(0);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your insights...</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-red-200">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Assessment Not Found</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/college/conversational-assessment')}
                className="w-full px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium"
              >
                Take the Assessment
              </button>
              <button
                onClick={() => navigate('/college')}
                className="w-full px-6 py-3 text-slate-600 hover:text-slate-900"
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check for new strategic format data
  // System now uses strategic format exclusively

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{
      background: 'linear-gradient(135deg, #fafaf9 0%, #f0fdfa 30%, #fef2f7 100%)'
    }}>
      {/* Background Pattern */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-0 w-full h-full opacity-40">
          <div className="absolute top-1/4 left-1/6 w-96 h-96 bg-teal-100 rounded-full blur-3xl opacity-30"></div>
          <div className="absolute top-3/4 right-1/6 w-80 h-80 bg-pink-100 rounded-full blur-3xl opacity-30"></div>
        </div>
      </div>

      {/* Back Link */}
      <div className="fixed top-8 left-8 z-50">
        <button 
          onClick={() => navigate('/college/parent-testing')}
          className="bg-white bg-opacity-90 backdrop-blur-sm border border-stone-200 text-stone-600 hover:text-teal-600 hover:bg-white transition-all px-5 py-3 rounded-xl text-sm font-medium flex items-center gap-2 shadow-lg"
        >
          <span>←</span>
          Back to Testing Hub
        </button>
      </div>

      <div className="container mx-auto px-8 py-16 max-w-6xl relative z-10">
        
        {/* Debug Info (only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="text-sm text-yellow-800">
              <strong>Debug Info:</strong> Data source: {dataSource} | Has insights: {insights ? 'Yes' : 'No'} | Loading: {isLoading ? 'Yes' : 'No'}
              {error && <div className="text-red-600 mt-1">Error: {error}</div>}
            </div>
          </div>
        )}

        {/* Testing Mode Indicator */}
        {isTestingMode && (
          <div className="bg-azure-50 border border-azure-200 rounded-lg p-4 mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-azure-600 text-white p-2 rounded-full">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div>
                <div className="text-azure-900 font-semibold">Testing Mode Active</div>
                <div className="text-sm text-azure-700">Your responses can be saved for future test runs</div>
              </div>
            </div>
          </div>
        )}

        <StrategicResultsRenderer 
          insights={insights?.insights || insights} 
          studentProfile={insights?.studentProfile}
          currentUser={currentUser} 
          studentGradeLevel={studentGradeLevel}
          integrationStatus={integrationStatus}
          addCollegeToList={addCollegeToList}
          addActionToTimeline={addActionToTimeline}
          isIntegrating={isIntegrating}
          calculateTimelineMessage={calculateTimelineMessage}
        />

        {/* Full markdown content removed - content now distributed to individual cards above */}

        {/* Permanent Save Status */}
        {(isSavingResults || savedSuccessfully || resultsPermalink) && (
          <div className="bg-white bg-opacity-95 backdrop-blur-xl border border-stone-200 rounded-2xl p-6 shadow-xl mb-8">
            {isSavingResults && (
              <div className="flex items-center justify-center gap-3 text-slate-600">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-600"></div>
                <span>Saving your results permanently...</span>
              </div>
            )}
            
            {savedSuccessfully && resultsPermalink && (
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-emerald-600 mb-3">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">Results saved permanently!</span>
                </div>
                <p className="text-slate-600 text-sm mb-3">
                  You can generate a strategic summary from your Student Profile Hub for AI-powered insights and recommendations.
                </p>
                <p className="text-slate-600 text-sm mb-4">
                  Your strategic profile has been saved and can be accessed anytime at:
                </p>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-slate-500" />
                  <code className="text-sm text-slate-700 flex-1">
                    /college/strategic-profile/{resultsPermalink}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(`${window.location.origin}/college/strategic-profile/${resultsPermalink}`)}
                    className="text-xs bg-slate-600 text-white px-2 py-1 rounded hover:bg-slate-700"
                  >
                    Copy Link
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Final Actions */}
        <div className="text-center space-y-4 sm:space-y-0 sm:flex sm:justify-center sm:gap-4 mt-12">
          <button
            onClick={goToDashboard}
            className="w-full sm:w-auto bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-900 hover:to-slate-900 text-white px-8 py-4 rounded-xl font-semibold transition-all transform hover:-translate-y-1 shadow-lg"
          >
            Take me to my dashboard
          </button>
          <button
            onClick={saveInsights}
            className="w-full sm:w-auto bg-white hover:bg-stone-50 text-slate-700 px-8 py-4 rounded-xl font-semibold border border-stone-200 transition-all transform hover:-translate-y-1 shadow-lg flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download as file
          </button>
          {currentUser?.role === 'child' && (
            <button
              onClick={() => setShowSharingModal(true)}
              className="w-full sm:w-auto bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white px-8 py-4 rounded-xl font-semibold transition-all transform hover:-translate-y-1 shadow-lg flex items-center justify-center gap-2"
            >
              <Share2 className="w-4 h-4" />
              {sharingPermissions.parentCanView ? 'Manage Sharing' : 'Share with Parent'}
            </button>
          )}
        </div>
      </div>

      {/* Sharing Modal */}
      {showSharingModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
            {/* Close button */}
            <button
              onClick={() => setShowSharingModal(false)}
              className="absolute top-4 right-4 text-stone-400 hover:text-stone-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Modal Header */}
            <div className="text-center mb-6">
              <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-3 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                {sharingPermissions.parentCanView ? 'Manage Sharing' : 'Share Your Results'}
              </h2>
              <p className="text-stone-600">
                {sharingPermissions.parentCanView 
                  ? 'Your results are currently shared with your parent'
                  : 'Let your parent see your strategic college profile'
                }
              </p>
            </div>

            {sharingPermissions.parentCanView ? (
              /* Already shared - show management options */
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
                  <Eye className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="text-emerald-800 font-medium">Results Shared</p>
                    <p className="text-emerald-600 text-sm">
                      Shared {sharingPermissions.sharedAt ? new Date(sharingPermissions.sharedAt).toLocaleDateString() : 'recently'}
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={handleStopSharing}
                  className="w-full bg-red-100 hover:bg-red-200 text-red-700 px-6 py-3 rounded-xl font-medium transition-colors"
                >
                  Stop Sharing
                </button>
              </div>
            ) : (
              /* Not shared - show sharing options */
              <div className="space-y-6">
                <div className="space-y-3">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={sharingPermissions.notifyParent}
                      onChange={(e) => setSharingPermissions(prev => ({
                        ...prev,
                        notifyParent: e.target.checked
                      }))}
                      className="w-4 h-4 text-teal-600 border-stone-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-slate-700">Notify parent when shared</span>
                  </label>
                </div>

                <div className="bg-azure-50 border border-azure-200 rounded-xl p-4">
                  <p className="text-azure-800 text-sm">
                    <strong>What your parent will see:</strong> Your complete strategic profile including strengths, 
                    positioning advice, college recommendations, and essay strategies.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowSharingModal(false)}
                    className="flex-1 bg-stone-100 hover:bg-stone-200 text-stone-700 px-6 py-3 rounded-xl font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleShareWithParent}
                    className="flex-1 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white px-6 py-3 rounded-xl font-medium transition-all"
                  >
                    Share Results
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationalResults;
